import { filter, map, pipe } from "ramda";
import XLSXPopulate from "xlsx-populate/browser/xlsx-populate";

import type { Item } from "./components/chackraSelectWithClear/chackraSelectWithClear";
import type { excelNames } from "./config/constants";
import type { ArkPagedQueryParameters } from "./lib/apiTypes";
import type { UserAccountInfo } from "./lib/authentication/authTypes";
import type { SelectedRow } from "./store/selectedRowsSlice";
export function getArtesianQueryParams(params: ArkPagedQueryParameters) {
  const { filters, pageIndex, pageSize } = params;

  const sorts = params.sorting
    ?.map(sorts => (sorts.id && sorts.id !== "" ? `${sorts.id} ${sorts.desc ? "desc" : "asc"}` : null))
    .join(",");

  // Transform filters array into an object
  const filtersObject = filters?.reduce<Record<string, unknown>>((acc, filter) => {
    acc[filter.id] = filter.value; // Keep the array as-is for now
    return acc;
  }, {});

  // Add skip and limit to the filters object
  const artesianFilters = {
    ...filtersObject,
    page: pageIndex ?? 0 + 1, // Adjust for 0-based index
    pageSize: pageSize,
    ...(sorts ? { sorts: sorts } : {}), // Add sorts without encoding
  };

  // Convert the filters object into query parameters
  const queryParams = new URLSearchParams();

  Object.entries(artesianFilters).forEach(([key, value]) => {
    if (key === "sorts") {
      // Do not encode the "sorts" parameter
      queryParams.append(key, String(value));
    } else if (Array.isArray(value)) {
      // Add each array element as a separate query parameter
      value.forEach(v => {
        queryParams.append(key, String(v));
      });
    } else {
      queryParams.append(key, String(value));
    }
  });

  return queryParams.toString(); // Return the query string
}

export function getOptionsFromEnumValues(
  enumObject: Record<string, string>,
  parser?: (value: string) => string,
  excludeValues?: string[],
): Item[] {
  return Object.entries(enumObject)
    .filter(([value]) => !excludeValues?.includes(value)) // Filter out excluded values
    .map(
      ([key, value]) =>
        ({
          label: parser ? parser(value) : value,
          value: key,
        }) as Item,
    );
}

export function getTenant(user: UserAccountInfo | null | undefined): string {
  const storedTenant = localStorage.getItem("selectedTenant");
  const availableTenants = user?.tenants ?? [];

  // Check if stored tenant is still valid
  if (storedTenant && availableTenants.includes(storedTenant)) {
    return storedTenant;
  }
  // If stored tenant is invalid or doesn't exist, use first available tenant
  const firstTenant = availableTenants[0] ?? "no tenant";

  // Update localStorage with the valid tenant (or remove if no tenant available)
  if (firstTenant !== "no tenant") {
    localStorage.setItem("selectedTenant", firstTenant);
  } else {
    localStorage.removeItem("selectedTenant");
  }

  return firstTenant;
}

export const getExcelName = (
  list: { name: string; excel: string; templateName: string; type: string | null }[],
  type: string,
  name: keyof (typeof excelNames)[0],
) => {
  // Trim the type to handle any accidental spaces
  const trimmedType = type.trim();

  // Find matching items and extract the requested property
  const ret = pipe(
    filter((item: (typeof excelNames)[number]) => item.name === trimmedType),
    map((item: (typeof excelNames)[number]) => item[name]),
  )(list);

  // Return the first match or a fallback value if no matches
  return ret.length > 0 ? ret[0] : "";
};

export async function downloadAndModifyExcelFromBlobStorage({
  generatedUrl,
  downloadUrl,
  excelType,
  templateName,
  type,
}: {
  generatedUrl: string;
  downloadUrl: string;
  excelType: string;
  templateName: string;
  type: string;
  isFilterId: boolean;
  curveList?: SelectedRow[];
}) {
  try {
    const response = await fetch(`${downloadUrl}${templateName}.xlsx`);
    if (!response.ok) throw new Error("Failed to download Excel template");
    const arrayBuffer = await response.arrayBuffer();

    const workbook = await XLSXPopulate.fromDataAsync(arrayBuffer);

    const queriesSheet = workbook.sheet("Queries");
    queriesSheet.cell("A2").value(generatedUrl);
    queriesSheet.cell("C2").value(type);


    const wbout = await workbook.outputAsync("arraybuffer");
    const blob = new Blob([wbout], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${excelType}${type || ""}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error("Error handling Excel download:", error);
  }
}

export function generateGridPointOptionsByLabel(label: string, selectedYear?: number, selectedDay?: Date): Item[] {
  console.log("GenerateGridPointOptionsByLabel", label, selectedYear, selectedDay);
  const year = selectedDay ? selectedDay.getFullYear() : (selectedYear ?? new Date().getFullYear());
  const shortYear = String(year).slice(-2);
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  switch (label) {
    case "M":
      return months.map(m => {
        const value = `${m}-${shortYear}`;
        return { value, label: value };
      });

    case "Year":
      return [
        { value: `GasYr-${shortYear}`, label: `GasYr-${shortYear}` },
        { value: `${year}`, label: `${year}` },
      ];

    case "Q":
      return [1, 2, 3, 4].map(q => {
        const value = `Q${q}${shortYear}`;
        return { value, label: value };
      });

    case "S":
      return [
        { value: `Win-${shortYear}`, label: `Win-${shortYear}` },
        { value: `Sum-${shortYear}`, label: `Sum-${shortYear}` },
      ];

    case "Wk":
      return Array.from({ length: 52 }, (_, i) => {
        const wk = String(i + 1).padStart(2, "0");
        const value = `Wk${wk}-${shortYear}`;
        return { value, label: value };
      });

    case "WkEnd":
      return Array.from({ length: 52 }, (_, i) => {
        const wk = String(i + 1).padStart(2, "0");
        const value = `WkEnd${wk}-${shortYear}`;
        return { value, label: value };
      });

    case "D": {
      const dayAbbr = selectedDay?.toLocaleDateString("en-US", { weekday: "short" });
      const iso = selectedDay?.toISOString().split("T")[0]; // e.g. 2025-05-02
      const value = `${dayAbbr}${iso}`;
      return [{ value, label: value }];
    }

    default:
      return [];
  }
}
